<script lang="ts" generics="T">
  import type { Snippet } from 'svelte';

  let {
    items,
    itemHeight = 56,
    overscan = 4,
    virtual = true,
    children
  }: {
    items: T[];
    /** Estimated height (px) for rows that have not been measured yet. */
    itemHeight?: number;
    overscan?: number;
    virtual?: boolean;
    children: Snippet<[T]>;
  } = $props();

  let scrollTop = $state(0);
  let containerHeight = $state(0);

  // Measured height per item index; 0 = not yet measured -> fall back to `itemHeight`.
  let heights = $state<number[]>([]);

  // Keep the heights cache aligned with the current item count, preserving
  // already-measured values (positionally) when the list grows or shrinks.
  $effect(() => {
    if (heights.length !== items.length) {
      const next = new Array<number>(items.length).fill(0);
      const keep = Math.min(heights.length, items.length);
      for (let i = 0; i < keep; i++) next[i] = heights[i];
      heights = next;
    }
  });

  // Prefix sums of real (or estimated) heights: prefix[i] = top offset of row i,
  // prefix[n] = total content height. Recomputed whenever a measurement changes.
  const prefix = $derived.by(() => {
    const n = items.length;
    const p = new Array<number>(n + 1);
    p[0] = 0;
    for (let i = 0; i < n; i++) p[i + 1] = p[i] + (heights[i] || itemHeight);
    return p;
  });

  const totalHeight = $derived(prefix[items.length] ?? 0);

  // First row whose bottom edge is past scrollTop (binary search over prefix sums).
  function findStart(y: number): number {
    let lo = 0;
    let hi = items.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (prefix[mid + 1] <= y) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  const startIndex = $derived(Math.max(0, findStart(scrollTop) - overscan));
  const endIndex = $derived.by(() => {
    const bottom = scrollTop + containerHeight;
    let i = startIndex;
    while (i < items.length && prefix[i] < bottom) i++;
    return Math.min(items.length - 1, i + overscan);
  });

  const visibleItems = $derived(items.slice(startIndex, endIndex + 1));
  const offsetTop = $derived(prefix[startIndex] ?? 0);
  const offsetBottom = $derived(Math.max(0, totalHeight - (prefix[endIndex + 1] ?? totalHeight)));

  // Measures a rendered row and keeps `heights[index]` in sync with its real,
  // possibly changing (expand/collapse) height via a ResizeObserver.
  function measure(node: HTMLElement, index: number) {
    let current = index;
    const apply = () => {
      const h = node.offsetHeight;
      if (h > 0 && heights[current] !== h) heights[current] = h;
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(node);
    return {
      update(newIndex: number) {
        current = newIndex;
        apply();
      },
      destroy() {
        ro.disconnect();
      }
    };
  }
</script>

{#if virtual}
<div
  class="flex-1 min-h-0 overflow-y-auto"
  style="overflow-anchor: none;"
  bind:clientHeight={containerHeight}
  onscroll={(e) => { scrollTop = (e.currentTarget as HTMLElement).scrollTop; }}
>
  <div style="height: {offsetTop}px;"></div>
  {#each visibleItems as item, i (startIndex + i)}
    <div use:measure={startIndex + i}>
      {@render children(item)}
    </div>
  {/each}
  <div style="height: {offsetBottom}px;"></div>
</div>
{:else}
<div class="flex-1 min-h-0 overflow-y-auto">
  {#each items as item}
    {@render children(item)}
  {/each}
</div>
{/if}
